// Account-holder request form — reachable only via RequireCustomer in
// App.jsx (a live 'customer'-role session). Reuses the exact same
// RequestLinesForm/RequestConfirmation components the guest path's final
// two steps use; this screen's only job is wiring them to POST /requests
// instead of POST /guest/requests (no email/OTP/organization-details steps
// here at all — an account holder's organization_id already lives on their
// session, server-side).

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import PortalShell from '../components/PortalShell.jsx';
import RequestLinesForm from '../components/RequestLinesForm.jsx';
import RequestConfirmation from '../components/RequestConfirmation.jsx';
import usePersistedState from '../hooks/usePersistedState.js';
import { mergePendingCatalogItems } from '../utils/pendingLine.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './RequestFormPage.module.css';

const LINES_KEY = 'sneethel.portal.account.lines';

// A plain JSON POST when there's no file (unchanged from before), or
// multipart/form-data when there is one — `lines` gets JSON-encoded as a
// text field either way the server can parse it back
// (backend/src/utils/requestUploadMulter.js#parseLinesField). See
// shared/api/client.js's own FormData branch (added for the admin product-
// photo upload, entry 42) — this reuses that same existing capability, not
// a new one.
function buildRequestBody({ neededBy, purpose, buyerNote, lines, file }) {
  if (!file) {
    return { neededBy, purpose, buyerNote, lines };
  }
  const formData = new FormData();
  if (neededBy) formData.append('neededBy', neededBy);
  if (purpose) formData.append('purpose', purpose);
  if (buyerNote) formData.append('buyerNote', buyerNote);
  formData.append('lines', JSON.stringify(lines));
  formData.append('file', file);
  return formData;
}

async function submitRequest(values) {
  return apiClient.post('/requests', buildRequestBody(values));
}

export default function RequestFormPage() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [linesDraft, setLinesDraft, clearLinesDraft] = usePersistedState(
    LINES_KEY,
    null,
    mergePendingCatalogItems
  );
  const [refNo, setRefNo] = useState(null);

  const submitMutation = useMutation({ mutationFn: submitRequest });
  const { toastOk, toastErr } = useActionFeedback();

  const linesDefaultValues = useMemo(
    () => linesDraft || { neededBy: '', purpose: '', buyerNote: '', lines: [] },
    [linesDraft]
  );

  const handleSubmit = (values) => {
    submitMutation.mutate(values, {
      onSuccess: ({ request }) => {
        clearLinesDraft();
        setRefNo(request.ref_no);
        toastOk(t('portal.requestForm.submittedToast', { refNo: request.ref_no }));
      },
      onError: (err) => toastErr(err, ['NO_ORGANIZATION', 'EMPTY_REQUEST', 'VALIDATION_ERROR']),
    });
  };

  if (refNo) {
    return (
      <PortalShell width="narrow">
        <RequestConfirmation refNo={refNo} onNewRequest={() => setRefNo(null)} />
      </PortalShell>
    );
  }

  return (
    <PortalShell width="wide">
      <div className={styles.accountBar}>
        <span className={styles.signedInAs}>
          {t('portal.requestForm.signedInAs', { name: user?.full_name || '' })}
        </span>
        <div className={styles.accountBarActions}>
          <Link to="/track" className={styles.myRequestsLink}>
            {t('portal.account.myRequestsLink')}
          </Link>
          <button type="button" className={styles.logoutButton} onClick={() => logout()}>
            {t('nav.logout')}
          </button>
        </div>
      </div>
      <RequestLinesForm
        defaultValues={linesDefaultValues}
        onDraftChange={setLinesDraft}
        onSubmit={handleSubmit}
        isSubmitting={submitMutation.isPending}
      />
    </PortalShell>
  );
}
